import type { RecommendationItem } from "@albertine/types/openapi/lmt";
import { ImageThumbnail } from "@prototypes/image-thumbnail/ImageThumbnail";
import classNames from "classnames";
import { categoryDisplayName } from "../../common/types/OfferingCategory";
import Label from "../Common/Label";
import "./SearchResultItem.css";

type WebRecommendationItem = Extract<RecommendationItem, { source: "web" }>;

type WebSearchResultItemProps = {
    className?: string;
    item: WebRecommendationItem;
    selectedItemId?: string;
    onClick: (recommendationId: string) => void;
    onHover?: (id: string | undefined) => void;
    highlightedItemId?: string;
};

export function WebSearchResultItem(props: WebSearchResultItemProps) {
    const { className, item, selectedItemId, onClick, onHover, highlightedItemId } =
        props;

    return (
        <div
            className={classNames(
                "search-result-item flex w-full items-start gap-4",
                {
                    "search-result-item--selected": selectedItemId === item.id,
                    "search-result-item--highlighted": highlightedItemId === item.id,
                },
                className,
            )}
            onClick={() => {
                onClick(item.id);
            }}
            onMouseEnter={() => {
                onHover?.(item.id);
            }}
            onMouseLeave={() => {
                onHover?.(undefined);
            }}
        >
            <ImageThumbnail variant="web" className="size-14 shrink-0 rounded-md" />
            <div className="search-result-item__details flex w-full flex-col items-start justify-start gap-2">
                <div className="flex w-full items-start justify-between gap-3">
                    <h4>{item.name}</h4>
                    <div className="flex shrink-0 flex-col items-end gap-1 pt-0.5">
                        {item.score != null && item.score > 0 ? (
                            <span className="font-medium text-xs text-zinc-900 leading-5">
                                {Math.round(item.score * 10)}%
                            </span>
                        ) : null}
                    </div>
                </div>
                <div className="search-result-item__categories">
                    <Label.Medium.Light.N50>
                        {categoryDisplayName(item.category, "en")}
                    </Label.Medium.Light.N50>
                </div>
                <Label.Small.SemiBold>{formatLocation(item)}</Label.Small.SemiBold>
            </div>
        </div>
    );
}

function formatLocation(item: WebRecommendationItem): string {
    const parts = [item.city, item.country].filter(
        (part): part is string => typeof part === "string" && part.length > 0,
    );
    return parts.length > 0 ? parts.join(", ") : "Location unknown";
}
